import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '../../../ui/core';
import { TripDisplayStatusOrder } from '../../../models/Trips';
import { TripDisplayStatusIcon } from './TripDisplayStatus';
import ResponsiveSheet from '../../shared/ResponsiveSheet';

export const NO_FILTERS = { status: null, citiesIds: [], ghostTripsOnly: false, pendingReassignmentOnly: false };

/** One per kind of filter set — what the Filters pill's badge shows. */
export const countActiveFilters = ({ status, citiesIds, ghostTripsOnly, pendingReassignmentOnly }) =>
  [!!status, citiesIds.length > 0, ghostTripsOnly, pendingReassignmentOnly].filter(Boolean).length;

const SectionLabel = ({ children }) => (
  <Typography
    sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: '4px' }}
  >
    {children}
  </Typography>
);

// Every Trips filter in one place: a dropdown on desktop, a bottom sheet on a
// phone. `value` is the URL's filter state; `onChange` takes a patch of it.
//
// Status counts come from the caller, computed with every filter but status
// applied — otherwise picking one status would zero out all the others.
const TripsFilters = ({ open, anchorEl, onClose, value, onChange, statusCounts, citiesOptions }) => {
  const { status, citiesIds, ghostTripsOnly, pendingReassignmentOnly } = value;

  const cities = useMemo(
    () =>
      [...citiesOptions].sort((a, b) => a.stateAbbr.localeCompare(b.stateAbbr) || a.cityName.localeCompare(b.cityName)),
    [citiesOptions],
  );

  const toggleCity = (id) =>
    onChange({ citiesIds: citiesIds.includes(id) ? citiesIds.filter((c) => c !== id) : [...citiesIds, id] });

  return (
    <ResponsiveSheet open={open} onClose={onClose} dropdown anchorEl={anchorEl}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', py: '12px' }}>
        <Box sx={{ flex: 1 }}>Filters</Box>
        <Button size="small" disabled={!countActiveFilters(value)} onClick={() => onChange(NO_FILTERS)}>
          Clear all
        </Button>
      </DialogTitle>
      <DialogContent dividers sx={{ width: { md: 340 }, px: '16px' }}>
        <Stack spacing="16px">
          <Box>
            <SectionLabel>Status</SectionLabel>
            <List dense disablePadding>
              {TripDisplayStatusOrder.map((s) => {
                const count = statusCounts[s] || 0;
                return (
                  <ListItemButton
                    key={s}
                    selected={status === s}
                    // Tapping the picked status again goes back to all of them.
                    onClick={() => onChange({ status: status === s ? null : s })}
                    sx={{ borderRadius: '8px', gap: '10px', opacity: count ? 1 : 0.4 }}
                  >
                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                      <TripDisplayStatusIcon status={s} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, fontSize: '14px' }}>{s}</Box>
                    <Box sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>{count}</Box>
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
          <Box>
            <SectionLabel>Cities</SectionLabel>
            {cities.length ? (
              <Stack>
                {cities.map((c) => (
                  <FormControlLabel
                    key={c.id}
                    control={
                      <Checkbox size="small" checked={citiesIds.includes(c.id)} onChange={() => toggleCity(c.id)} />
                    }
                    label={`${c.cityName}, ${c.stateAbbr}`}
                  />
                ))}
              </Stack>
            ) : (
              <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>No trips this day.</Typography>
            )}
          </Box>
          <Box>
            <SectionLabel>Only show</SectionLabel>
            <Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={ghostTripsOnly}
                    onChange={(e) => onChange({ ghostTripsOnly: e.target.checked })}
                  />
                }
                label="Ghost trips"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={pendingReassignmentOnly}
                    onChange={(e) => onChange({ pendingReassignmentOnly: e.target.checked })}
                  />
                }
                label="Pending reassignment"
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </ResponsiveSheet>
  );
};

export default TripsFilters;
